import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Shield, Coins, Users, Award, ArrowRight, Wallet as WalletIcon,
  ChevronRight, FlaskConical, Cpu, Bot, Scale, Sparkles, Server,
  TrendingUp, Globe, CheckCircle2, Menu, Languages, Headphones
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWallet } from "@/contexts/WalletContext";
import { WalletConnectModal } from "@/components/WalletConnectModal";
import { SignupModal, LoginModal } from "@/components/AuthModals";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { openTelegramSupport } from "@/components/TelegramFAB";
import api from "@/lib/api";
import { toast } from "sonner";

import { Wallet as WalletIcon2, Coins as CoinsIcon, Users as UsersIcon, Award as AwardIcon, LifeBuoy as SupportIcon, Home as HomeIcon } from "lucide-react";

// Top section: app navigation (routes)
const APP_NAV = [
  { to: "/",         label: "Home",     icon: HomeIcon },
  { to: "/wallet",   label: "Wallet",   icon: WalletIcon2 },
  { to: "/staking",  label: "Staking",  icon: CoinsIcon },
  { to: "/team",     label: "Team",     icon: UsersIcon },
  { to: "/rank",     label: "VIP Rank", icon: AwardIcon },
  { to: "/support",  label: "Support",  icon: SupportIcon },
];

// Bottom section: scroll-to sections on landing
const SECTIONS_NAV = [
  { id: "about",       label: "About" },
  { id: "why",         label: "Why SMART HUB" },
  { id: "participate", label: "Participate" },
  { id: "global",      label: "Global" },
  { id: "partners",    label: "Partners" },
];

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "zh", label: "中文" },
  { code: "ko", label: "한국어" },
  { code: "ja", label: "日本語" },
  { code: "es", label: "Español" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "pt", label: "Português" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { token, hydrated } = useAuth();
  const wallet = useWallet();
  const [open, setOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [mode, setMode] = useState("login"); // "signup" | "login"
  const [lang, setLang] = useState("en");
  const referrer = params.get("ref");
  const hasReferrer = !!(referrer && referrer.trim());

  useEffect(() => {
    if (hydrated && token) navigate("/dashboard");
  }, [hydrated, token, navigate]);

  const handleSignup = () => {
    if (token) return navigate("/dashboard");
    if (!hasReferrer) {
      toast.error(
        "Referral link required to sign up. Please use a sponsor's referral link.",
        { duration: 5000 }
      );
      return;
    }
    setSignupOpen(true);
  };

  const handleLogin = () => {
    if (token) return navigate("/dashboard");
    setLoginOpen(true);
  };

  // legacy alias (kept in case any old button still calls it)
  const handleConnect = handleLogin;

  const handleDevLogin = async () => {
    try {
      if (!hasReferrer) {
        toast.error("Referral link required for demo signup.");
        return;
      }
      const rand = "0x" + Array.from(crypto.getRandomValues(new Uint8Array(20)))
        .map((b) => b.toString(16).padStart(2, "0")).join("");
      const r = await api.post("/auth/dev-login", { address: rand, referrer });
      localStorage.setItem("umakant_token", r.data.token);
      localStorage.setItem("umakant_user", JSON.stringify(r.data.user));
      toast.success("Demo session started");
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo login failed");
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-[var(--surface-0)] text-[var(--text-1)]">
      {/* Background ambient */}
      <div className="absolute inset-0 umakant-hero-bg pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      {/* Top nav */}
      <header className="relative z-20 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Logo size={40} glow />
          <span className="font-semibold tracking-wide text-lg">SMART HUB</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            data-testid="topbar-signup-button"
            onClick={handleSignup}
            disabled={!hasReferrer}
            className="font-semibold h-10 px-4 text-sm text-white hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: hasReferrer ? "var(--gradient-primary)" : "rgba(124,58,237,0.25)",
              boxShadow: hasReferrer ? "0 6px 16px -4px rgba(124,58,237,0.5)" : "none",
            }}
          >
            <Sparkles size={14} className="mr-1.5" /> Sign Up
          </Button>
          <Button
            data-testid="topbar-login-button"
            onClick={handleLogin}
            variant="outline"
            className="font-semibold h-10 px-3 text-sm border-[rgba(124,58,237,0.4)] text-[var(--purple-300)] hover:bg-[rgba(124,58,237,0.10)] hover:text-[var(--purple-200)] hidden sm:inline-flex"
          >
            <WalletIcon size={14} className="mr-1.5" /> Login
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-select-button" className="w-10 h-10 rounded-xl bg-[var(--surface-2)] border border-white/10 hover:border-white/20 flex items-center justify-center transition-colors" aria-label="Language">
                <Languages size={18} className="text-[var(--text-2)]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[var(--surface-2)] border-white/10 text-[var(--text-1)]">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`cursor-pointer ${lang === l.code ? "text-[var(--purple-400)]" : ""}`}>
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="hamburger-menu-button" className="w-10 h-10 rounded-xl bg-[var(--surface-2)] border border-white/10 hover:border-white/20 flex items-center justify-center transition-colors" aria-label="Menu">
                <Menu size={18} className="text-[var(--text-2)]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[var(--surface-1)] border-white/10 text-[var(--text-1)] w-[280px]">
              <div className="flex items-center gap-2 mb-6 mt-2">
                <Logo size={36} glow />
                <span className="font-semibold">SMART HUB</span>
              </div>
              <nav className="space-y-1">
                {/* Home is always visible */}
                <button
                  onClick={() => scrollTo("home")}
                  className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 transition-colors"
                  data-testid="nav-home"
                >
                  <HomeIcon size={16} className="text-[var(--purple-400)]" />
                  <span>Home</span>
                </button>

                {/* Support — opens Telegram 1-on-1 chat with support team (username hidden) */}
                <button
                  onClick={openTelegramSupport}
                  className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 transition-colors group"
                  data-testid="nav-support"
                  aria-label="Open Telegram support chat"
                >
                  <Headphones size={16} className="text-[#29B6F6] transition-transform group-hover:scale-110" />
                  <span className="flex-1">Support</span>
                  <span
                    className="text-[9px] mono uppercase tracking-[0.10em] px-1.5 py-0.5 rounded-md"
                    style={{
                      background: "rgba(41, 182, 246, 0.12)",
                      color: "#4FC3F7",
                      border: "1px solid rgba(41, 182, 246, 0.30)",
                    }}
                  >
                    Telegram
                  </span>
                </button>

                {/* Protected routes - only when logged in */}
                {token && APP_NAV.filter(i => i.to !== "/").map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm text-[var(--text-2)] hover:text-[var(--text-1)] hover:bg-white/5 transition-colors"
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <Icon size={16} className="text-[var(--purple-400)]" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}

                <div className="h-px bg-white/10 my-3" />

                {token ? (
                  <button
                    onClick={() => { wallet.disconnect && wallet.disconnect(); navigate("/"); }}
                    className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold text-[var(--destructive)] hover:bg-[rgba(239,68,68,0.10)]"
                    data-testid="nav-disconnect"
                  >
                    Disconnect
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSignup}
                      disabled={!hasReferrer}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: hasReferrer ? "var(--gradient-primary)" : "rgba(124,58,237,0.25)",
                      }}
                      data-testid="nav-signup"
                    >
                      Sign Up
                    </button>
                    <button
                      onClick={handleLogin}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold text-[var(--purple-300)] border border-[rgba(124,58,237,0.4)] hover:bg-[rgba(124,58,237,0.10)]"
                      data-testid="nav-login"
                    >
                      Login
                    </button>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* HERO */}
      <section id="home" className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-12 pb-12 sm:pb-20">
        <div className="flex flex-col items-center text-center">
          {/* 3D SMART HUB hero medallion */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[260px] h-[260px] sm:w-[340px] sm:h-[340px] mb-6 sm:mb-8"
          >
            {/* Outer soft halo */}
            <div
              className="absolute inset-0 rounded-full pulse-glow"
              style={{
                background:
                  "radial-gradient(circle at 35% 30%, rgba(255,211,106,0.35) 0%, rgba(124,58,237,0.30) 35%, transparent 70%)",
                filter: "blur(2px)",
              }}
            />
            {/* Gold rotating conic ring (premium frame) */}
            <motion.div
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
              className="absolute inset-2 rounded-full"
              style={{
                background:
                  "conic-gradient(from 210deg, #FFD36A 0deg, #FFFFFF 60deg, #F3BA2F 130deg, #7C3AED 210deg, #FFD36A 290deg, #FFFFFF 340deg, #FFD36A 360deg)",
                padding: "3px",
                boxShadow:
                  "0 0 0 1px rgba(0,0,0,0.25), 0 18px 44px rgba(255,211,106,0.35), 0 8px 22px rgba(124,58,237,0.35)",
              }}
            >
              <div
                className="w-full h-full rounded-full"
                style={{ background: "var(--surface-1)" }}
              />
            </motion.div>

            {/* SMART HUB logo medallion (subtle floating motion) */}
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
              className="absolute inset-5 rounded-full overflow-hidden"
              style={{
                background:
                  "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 55%)",
                boxShadow:
                  "inset -18px -22px 60px rgba(0,0,0,0.55), inset 12px 14px 40px rgba(255,255,255,0.18), 0 24px 70px rgba(0,0,0,0.45)",
              }}
            >
              <picture>
                <source srcSet="/brand/smart-hub-logo.webp" type="image/webp" />
                <img
                  src="/brand/smart-hub-logo.png"
                  alt="SMART HUB"
                  draggable={false}
                  decoding="async"
                  fetchpriority="high"
                  className="w-full h-full object-contain select-none"
                  style={{ filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.55))" }}
                />
              </picture>
              {/* Glossy top sheen */}
              <div
                className="absolute top-2 left-6 right-12 h-10 rounded-full pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)",
                  filter: "blur(4px)",
                  opacity: 0.8,
                }}
              />
            </motion.div>

            {/* Orbit rings */}
            <div className="absolute -inset-4 rounded-full border border-[rgba(255,211,106,0.18)]" />
            <div className="absolute -inset-10 rounded-full border border-[rgba(124,58,237,0.15)]" />
            <div className="absolute -inset-16 rounded-full border border-[rgba(124,58,237,0.08)] hidden sm:block" />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="flex flex-col items-center">
            <Badge className="mb-4 bg-[var(--surface-2)] text-[var(--purple-300)] border border-[rgba(124,58,237,0.35)] hover:bg-[var(--surface-2)]">
              <Sparkles size={12} className="mr-1.5" /> Decentralized · BSC · USDT
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight max-w-3xl">
              The First Benchmark <br className="hidden sm:block" />
              Protocol on <span className="gradient-text">BSC Chain</span>
            </h1>
            <p className="text-[var(--text-2)] text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
              SMART HUB is a decentralized modular staking + 25-level team protocol for the new Web3 era. Stake USDT, grow your network, earn across 25 levels — all on BSC.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mt-7 w-full max-w-md sm:max-w-none">
              <Button
                data-testid="hero-signup-button"
                onClick={handleSignup}
                disabled={!hasReferrer}
                className="font-semibold h-12 px-6 text-white hover:opacity-95 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: hasReferrer ? "var(--gradient-primary)" : "rgba(124,58,237,0.25)",
                  boxShadow: hasReferrer ? "0 10px 30px rgba(124,58,237,0.4)" : "none",
                }}
              >
                <Sparkles size={16} className="mr-2" /> Sign Up
              </Button>
              <Button
                data-testid="hero-login-button"
                onClick={handleLogin}
                variant="outline"
                className="font-semibold h-12 px-6 w-full sm:w-auto border-[rgba(124,58,237,0.4)] text-[var(--purple-300)] hover:bg-[rgba(124,58,237,0.10)] hover:text-[var(--purple-200)]"
              >
                <WalletIcon size={16} className="mr-2" /> Login
              </Button>
            </div>

            {/* Referrer status indicator */}
            <div
              data-testid="referrer-status"
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium"
              style={{
                background: hasReferrer
                  ? "linear-gradient(180deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)"
                  : "linear-gradient(180deg, rgba(248,113,113,0.10) 0%, rgba(248,113,113,0.03) 100%)",
                border: hasReferrer
                  ? "1px solid rgba(34,197,94,0.35)"
                  : "1px solid rgba(248,113,113,0.35)",
                color: hasReferrer ? "#22C55E" : "#F87171",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: hasReferrer ? "#22C55E" : "#F87171",
                  boxShadow: `0 0 6px ${hasReferrer ? "#22C55E" : "#F87171"}`,
                }}
              />
              {hasReferrer ? (
                <>
                  Referral link active ·{" "}
                  <span className="mono opacity-80">
                    {referrer.slice(0, 6)}…{referrer.slice(-4)}
                  </span>
                </>
              ) : (
                <>Sign up requires a sponsor's referral link</>
              )}
            </div>

            <div className="text-[11px] text-[var(--text-3)] mt-3">
              Web3 wallet · No password · Signature-based authentication
            </div>
          </motion.div>
        </div>
      </section>

      <WalletConnectModal open={open} onOpenChange={setOpen} referrer={referrer} mode={mode} />

      <SignupModal
        open={signupOpen}
        onOpenChange={setSignupOpen}
        referrer={referrer}
        onSwitchToLogin={() => {
          setSignupOpen(false);
          setTimeout(() => setLoginOpen(true), 150);
        }}
      />
      <LoginModal
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onSwitchToSignup={() => {
          setLoginOpen(false);
          if (hasReferrer) {
            setTimeout(() => setSignupOpen(true), 150);
          } else {
            toast.error("Referral link required to sign up");
          }
        }}
      />
    </div>
  );
}
